import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { RefreshCw, Plane, TrendingUp, Clock, Calendar, Wrench, FileText, AlertCircle, Download, Target, Gauge, Sun, Moon, BarChart3, LineChartIcon, AreaChart, Activity } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart as RechartsAreaChart, Area, ComposedChart, Bar, ReferenceLine } from "recharts";
import { useTheme } from "@/context/ThemeContext";
import "./FleetStatus.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NSAS_LOGO = "https://customer-assets.emergentagent.com/job_preflight-weather/artifacts/t3bj072m_NSAS-RGB-zilver-baseline.png";

// Circular Gauge Component - Clean Design with better visibility
const CircularGauge = ({ value, max, label, unit, color, size = 140, darkMode = false }) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 150);
  const displayPercentage = Math.min(percentage, 100);
  const radius = 50;
  const strokeWidth = 10;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (displayPercentage / 100) * circumference;
  
  const getColor = () => {
    if (color) return color;
    if (percentage >= 100) return '#10b981';
    if (percentage >= 80) return '#3b82f6';
    if (percentage >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const displayValue = typeof value === 'number' ? value.toFixed(1) : value;
  const bgCircleColor = darkMode ? '#334155' : '#e5e7eb';
  const labelColor = darkMode ? '#e2e8f0' : '#64748b';

  return (
    <div className="gauge-wrapper">
      <svg height={size} width={size} viewBox="0 0 120 120">
        {/* Background circle */}
        <circle
          stroke={bgCircleColor}
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx="60"
          cy="60"
        />
        {/* Progress circle */}
        <circle
          stroke={getColor()}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference + ' ' + circumference}
          style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease' }}
          r={normalizedRadius}
          cx="60"
          cy="60"
          transform="rotate(-90 60 60)"
        />
        {/* Center text */}
        <text
          x="60"
          y="55"
          textAnchor="middle"
          dominantBaseline="middle"
          fill={getColor()}
          fontSize="24"
          fontWeight="700"
          fontFamily="JetBrains Mono, monospace"
        >
          {displayValue}
        </text>
        <text
          x="60"
          y="75"
          textAnchor="middle"
          dominantBaseline="middle"
          fill={labelColor}
          fontSize="12"
          fontWeight="600"
        >
          {unit}
        </text>
      </svg>
      <div className="gauge-label">{label}</div>
    </div>
  );
};

// NEW: Large KPI Card for clear presentation
const KPICard = ({ title, value, subtitle, trend, trendValue, color = 'blue', icon: Icon }) => {
  const colorClasses = {
    blue: { bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.3)', text: '#3b82f6' },
    green: { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)', text: '#10b981' },
    amber: { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', text: '#f59e0b' },
    red: { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)', text: '#ef4444' },
    purple: { bg: 'rgba(139, 92, 246, 0.1)', border: 'rgba(139, 92, 246, 0.3)', text: '#8b5cf6' },
  };
  
  const colors = colorClasses[color] || colorClasses.blue;
  
  return (
    <div className="kpi-card" style={{ background: colors.bg, borderColor: colors.border }}>
      <div className="kpi-header">
        {Icon && <Icon size={20} style={{ color: colors.text }} />}
        <span className="kpi-title">{title}</span>
      </div>
      <div className="kpi-value" style={{ color: colors.text }}>{value}</div>
      {subtitle && <div className="kpi-subtitle">{subtitle}</div>}
      {trend && (
        <div className={`kpi-trend ${trend === 'up' ? 'positive' : trend === 'down' ? 'negative' : ''}`}>
          {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} {trendValue}
        </div>
      )}
    </div>
  );
};

// Horizontal Progress Bar Component
const ProgressBar = ({ value, max, label, color, showValue = true }) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  
  const getColor = () => {
    if (color) return color;
    if (percentage >= 100) return '#10b981';
    if (percentage >= 80) return '#3b82f6';
    if (percentage >= 60) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="progress-bar-wrapper">
      <div className="progress-bar-header">
        <span className="progress-bar-label">{label}</span>
        {showValue && (
          <span className="progress-bar-value" style={{ color: getColor() }}>
            {value.toFixed(1)}%
          </span>
        )}
      </div>
      <div className="progress-bar-track">
        <div 
          className="progress-bar-fill" 
          style={{ width: `${percentage}%`, backgroundColor: getColor() }}
        />
      </div>
      <div className="progress-bar-labels">
        <span>0%</span>
        <span>50%</span>
        <span>100%</span>
      </div>
    </div>
  );
};

// Status Indicator Component
const StatusIndicator = ({ isAhead, daysVariance, hoursVariance }) => {
  const formatHours = (h) => {
    const hours = Math.floor(Math.abs(h));
    const mins = Math.floor((Math.abs(h) - hours) * 60);  // Use floor to match backend
    return `${h < 0 ? '-' : '+'}${hours}:${mins.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`status-indicator ${isAhead ? 'ahead' : 'behind'}`}>
      <div className="status-icon">
        {isAhead ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="18,15 12,9 6,15" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6,9 12,15 18,9" />
          </svg>
        )}
      </div>
      <div className="status-text">
        <span className="status-label">{isAhead ? 'AHEAD' : 'BEHIND'}</span>
        <span className="status-days">{Math.abs(daysVariance).toFixed(1)} days</span>
        <span className="status-hours">{formatHours(hoursVariance)}</span>
      </div>
    </div>
  );
};

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const formatHours = (value) => {
      if (value === null || value === undefined) return '-';
      const hours = Math.floor(value);
      const mins = Math.floor((value - hours) * 60);  // Use floor to match backend
      return `${hours}:${mins.toString().padStart(2, '0')}`;
    };
    
    return (
      <div className="chart-tooltip">
        <p className="tooltip-date">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="tooltip-value" style={{ color: entry.color }}>
            {entry.name}: <strong>{formatHours(entry.value)}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Chart Style Options
const CHART_STYLES = [
  { id: 'classic', name: 'Classic Lines', icon: LineChartIcon },
  { id: 'area', name: 'Gradient Area', icon: AreaChart },
  { id: 'neon', name: 'Neon Glow', icon: Activity },
  { id: 'minimal', name: 'Minimal', icon: BarChart3 },
];

// Trend Chart Component with Multiple Styles
const TrendChart = ({ trendData, contractedHours, chartStyle = 'classic', darkMode = false }) => {
  if (!trendData || !trendData.target || trendData.target.length < 2) {
    return <div className="chart-loading">No trend data available</div>;
  }

  const { actual, target } = trendData;
  const maxHours = contractedHours || 443;
  const contractStart = new Date(target[0].date);
  const contractEnd = new Date(target[target.length - 1].date);
  const totalDays = Math.ceil((contractEnd - contractStart) / (1000 * 60 * 60 * 24));

  const formatHoursLabel = (value) => {
    if (value === null || value === undefined) return '';
    const hours = Math.floor(value);
    const mins = Math.floor((value - hours) * 60);  // Use floor to match backend formatting
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  // Build chart data with DAILY data points for accurate hover tooltips
  const chartData = [];
  const actualMap = new Map();
  
  // Build map of actual data by date
  if (actual && actual.length > 0) {
    actual.forEach(d => {
      actualMap.set(d.date, d.hours);
    });
  }

  // Generate daily data points
  let currentDate = new Date(contractStart);
  let lastActualValue = 0;
  const today = new Date();
  
  while (currentDate <= contractEnd) {
    const dateStr = currentDate.toISOString().split('T')[0];
    const dayIndex = Math.ceil((currentDate - contractStart) / (1000 * 60 * 60 * 24));
    const targetValue = (dayIndex / totalDays) * maxHours;
    
    // Get actual value for this specific day
    let actualValue = null;
    
    if (actualMap.has(dateStr)) {
      lastActualValue = actualMap.get(dateStr);
      actualValue = lastActualValue;
    } else if (currentDate <= today && lastActualValue > 0) {
      // For past dates without data, carry forward last known value
      actualValue = lastActualValue;
    }
    
    // Only show actual line up to today
    if (currentDate > today) {
      actualValue = null;
    }
    
    // Calculate variance for each point
    const variance = actualValue !== null ? actualValue - targetValue : null;
    
    chartData.push({
      date: dateStr,
      dateLabel: currentDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      target: targetValue,
      actual: actualValue,
      variance: variance
    });
    
    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Ensure we have the end point
  if (chartData.length > 0 && chartData[chartData.length - 1].date !== contractEnd.toISOString().split('T')[0]) {
    chartData.push({
      date: contractEnd.toISOString().split('T')[0],
      dateLabel: contractEnd.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      target: maxHours,
      actual: null,
      variance: null
    });
  }

  const lastActual = actual && actual.length > 0 ? actual[actual.length - 1] : null;
  
  // Get expected hours from target data for today (accounts for rest days properly)
  const todayStr = today.toISOString().split('T')[0];
  const todayTarget = target.find(t => t.date === todayStr);
  const lastTarget = target && target.length > 0 ? target[target.length - 1] : null;
  
  // Use today's target if available, otherwise interpolate
  const expectedAtToday = todayTarget ? todayTarget.hours : 
    (lastTarget ? (Math.min(1, (today - contractStart) / (contractEnd - contractStart)) * maxHours) : 0);
  
  const variance = lastActual ? lastActual.hours - expectedAtToday : 0;

  // Theme-based colors
  const gridColor = darkMode ? '#334155' : '#f1f5f9';
  const axisColor = darkMode ? '#64748b' : '#94a3b8';
  const textColor = darkMode ? '#94a3b8' : '#64748b';

  // Style-specific configurations
  const styleConfig = {
    classic: {
      targetColor: '#3b82f6',
      actualColor: '#dc2626',
      targetStroke: 2,
      actualStroke: 2,
      gridDash: '3 3',
      showDots: false,
    },
    area: {
      targetColor: '#3b82f6',
      actualColor: '#10b981',
      targetStroke: 2,
      actualStroke: 3,
      gridDash: '0',
      showDots: false,
    },
    neon: {
      targetColor: '#06b6d4',
      actualColor: '#f43f5e',
      targetStroke: 3,
      actualStroke: 3,
      gridDash: '5 5',
      showDots: true,
    },
    minimal: {
      targetColor: '#94a3b8',
      actualColor: '#6366f1',
      targetStroke: 1,
      actualStroke: 2,
      gridDash: '0',
      showDots: false,
    },
  };

  const config = styleConfig[chartStyle] || styleConfig.classic;

  const renderChart = () => {
    switch (chartStyle) {
      case 'area':
        return (
          <RechartsAreaChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 50 }}>
            <defs>
              <linearGradient id="targetGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={config.targetColor} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={config.targetColor} stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={config.actualColor} stopOpacity={0.5}/>
                <stop offset="95%" stopColor={config.actualColor} stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray={config.gridDash} stroke={gridColor} />
            <XAxis 
              dataKey="dateLabel" 
              stroke={axisColor} 
              tick={{ fill: textColor, fontSize: 10 }}
              tickLine={{ stroke: gridColor }}
              angle={-45}
              textAnchor="end"
              height={50}
              interval={Math.max(0, Math.floor(chartData.length / 12))}
            />
            <YAxis 
              stroke={axisColor} 
              tick={{ fill: textColor, fontSize: 10 }}
              tickLine={{ stroke: gridColor }}
              domain={[0, maxHours]}
              tickFormatter={formatHoursLabel}
              tickCount={8}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '8px' }}
              formatter={(value) => <span style={{ color: textColor, fontSize: '0.75rem' }}>{value}</span>}
            />
            <Area 
              type="monotone" 
              dataKey="target" 
              name="Target" 
              stroke={config.targetColor} 
              strokeWidth={config.targetStroke}
              fill="url(#targetGradient)"
              connectNulls={true}
            />
            <Area 
              type="monotone" 
              dataKey="actual" 
              name="Actual" 
              stroke={config.actualColor} 
              strokeWidth={config.actualStroke}
              fill="url(#actualGradient)"
              connectNulls={false}
            />
          </RechartsAreaChart>
        );

      case 'neon':
        return (
          <LineChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 50 }}>
            <defs>
              <filter id="neonGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
              <linearGradient id="neonTargetLine" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#06b6d4"/>
                <stop offset="50%" stopColor="#22d3ee"/>
                <stop offset="100%" stopColor="#06b6d4"/>
              </linearGradient>
              <linearGradient id="neonActualLine" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#f43f5e"/>
                <stop offset="50%" stopColor="#fb7185"/>
                <stop offset="100%" stopColor="#f43f5e"/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray={config.gridDash} stroke={darkMode ? '#1e293b' : '#e2e8f0'} />
            <XAxis 
              dataKey="dateLabel" 
              stroke={axisColor} 
              tick={{ fill: textColor, fontSize: 10 }}
              tickLine={{ stroke: gridColor }}
              angle={-45}
              textAnchor="end"
              height={50}
              interval={Math.max(0, Math.floor(chartData.length / 12))}
            />
            <YAxis 
              stroke={axisColor} 
              tick={{ fill: textColor, fontSize: 10 }}
              tickLine={{ stroke: gridColor }}
              domain={[0, maxHours]}
              tickFormatter={formatHoursLabel}
              tickCount={8}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '8px' }}
              formatter={(value) => <span style={{ color: textColor, fontSize: '0.75rem' }}>{value}</span>}
            />
            <Line 
              type="monotone" 
              dataKey="target" 
              name="Target" 
              stroke="url(#neonTargetLine)" 
              strokeWidth={config.targetStroke}
              dot={config.showDots ? { fill: config.targetColor, stroke: '#fff', strokeWidth: 2, r: 3 } : false}
              activeDot={{ r: 6, fill: config.targetColor, stroke: '#fff', strokeWidth: 2, filter: 'url(#neonGlow)' }}
              connectNulls={true}
              filter="url(#neonGlow)"
            />
            <Line 
              type="monotone" 
              dataKey="actual" 
              name="Actual" 
              stroke="url(#neonActualLine)" 
              strokeWidth={config.actualStroke}
              dot={config.showDots ? { fill: config.actualColor, stroke: '#fff', strokeWidth: 2, r: 3 } : false}
              activeDot={{ r: 6, fill: config.actualColor, stroke: '#fff', strokeWidth: 2, filter: 'url(#neonGlow)' }}
              connectNulls={false}
              filter="url(#neonGlow)"
            />
          </LineChart>
        );

      case 'minimal':
        return (
          <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 50 }}>
            <CartesianGrid strokeDasharray={config.gridDash} stroke={gridColor} vertical={false} />
            <XAxis 
              dataKey="dateLabel" 
              stroke={axisColor} 
              tick={{ fill: textColor, fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: gridColor }}
              angle={-45}
              textAnchor="end"
              height={50}
              interval={Math.max(0, Math.floor(chartData.length / 12))}
            />
            <YAxis 
              stroke={axisColor} 
              tick={{ fill: textColor, fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              domain={[0, maxHours]}
              tickFormatter={formatHoursLabel}
              tickCount={6}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '8px' }}
              formatter={(value) => <span style={{ color: textColor, fontSize: '0.75rem' }}>{value}</span>}
            />
            <ReferenceLine y={expectedAtToday} stroke={darkMode ? '#475569' : '#cbd5e1'} strokeDasharray="8 4" label={{ value: 'Today', fill: textColor, fontSize: 10, position: 'right' }} />
            <Line 
              type="stepAfter" 
              dataKey="target" 
              name="Target" 
              stroke={config.targetColor} 
              strokeWidth={config.targetStroke}
              strokeDasharray="8 4"
              dot={false}
              activeDot={{ r: 4, fill: config.targetColor, stroke: '#fff', strokeWidth: 1 }}
              connectNulls={true}
            />
            <Line 
              type="monotone" 
              dataKey="actual" 
              name="Actual" 
              stroke={config.actualColor} 
              strokeWidth={config.actualStroke}
              dot={false}
              activeDot={{ r: 5, fill: config.actualColor, stroke: '#fff', strokeWidth: 2 }}
              connectNulls={false}
            />
          </ComposedChart>
        );

      default: // classic
        return (
          <LineChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 50 }}>
            <CartesianGrid strokeDasharray={config.gridDash} stroke={gridColor} />
            <XAxis 
              dataKey="dateLabel" 
              stroke={axisColor} 
              tick={{ fill: textColor, fontSize: 10 }}
              tickLine={{ stroke: gridColor }}
              angle={-45}
              textAnchor="end"
              height={50}
              interval={Math.max(0, Math.floor(chartData.length / 12))}
            />
            <YAxis 
              stroke={axisColor} 
              tick={{ fill: textColor, fontSize: 10 }}
              tickLine={{ stroke: gridColor }}
              domain={[0, maxHours]}
              tickFormatter={formatHoursLabel}
              tickCount={8}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '8px' }}
              formatter={(value) => <span style={{ color: textColor, fontSize: '0.75rem' }}>{value}</span>}
            />
            <Line 
              type="linear" 
              dataKey="target" 
              name="Target" 
              stroke={config.targetColor} 
              strokeWidth={config.targetStroke}
              dot={false}
              activeDot={{ r: 4, fill: config.targetColor, stroke: '#fff', strokeWidth: 2 }}
              connectNulls={true}
            />
            <Line 
              type="monotone" 
              dataKey="actual" 
              name="Actual" 
              stroke={config.actualColor} 
              strokeWidth={config.actualStroke}
              dot={false}
              activeDot={{ r: 4, fill: config.actualColor, stroke: '#fff', strokeWidth: 2 }}
              connectNulls={false}
            />
          </LineChart>
        );
    }
  };

  return (
    <div className="chart-wrapper">
      <ResponsiveContainer width="100%" height={400}>
        {renderChart()}
      </ResponsiveContainer>
      {lastActual && (
        <div className="chart-summary">
          <span className="summary-label">Flown:</span>
          <span className="summary-value actual">{formatHoursLabel(lastActual.hours)}</span>
          <span className="summary-separator">|</span>
          <span className="summary-label">Expected:</span>
          <span className="summary-value target">{formatHoursLabel(expectedAtToday)}</span>
          <span className="summary-separator">|</span>
          <span className="summary-label">Variance:</span>
          <span className={`summary-value ${variance >= 0 ? 'ahead' : 'behind'}`}>
            {variance >= 0 ? '+' : ''}{formatHoursLabel(Math.abs(variance))} {variance >= 0 ? 'ahead' : 'behind'}
          </span>
        </div>
      )}
    </div>
  );
};

// 3D Variance Bar Chart Component - Shows daily variance with 3D effect
const VarianceBarChart3D = ({ trendData, darkMode = false, hasFixedRestDay = true }) => {
  if (!trendData || !trendData.actual || !trendData.target) {
    return <div className="chart-loading">No variance data available</div>;
  }

  const { actual, target } = trendData;
  
  // Build maps for quick lookup
  const actualMap = new Map();
  actual.forEach(d => actualMap.set(d.date, d.hours));
  
  const targetMap = new Map();
  target.forEach(d => targetMap.set(d.date, { hours: d.hours, isRestDay: d.isRestDay }));
  
  // Calculate daily variance (hours gained/lost each day)
  // Rest days are determined by the API based on aircraft config
  const varianceData = [];
  let prevActual = 0;
  let prevTarget = 0;
  
  // Get all dates from actual data (days we have data for)
  const today = new Date().toISOString().split('T')[0];
  
  actual.forEach((point, index) => {
    const date = point.date;
    if (date > today) return; // Don't show future dates
    
    const currentActual = point.hours;
    const targetData = targetMap.get(date) || { hours: 0, isRestDay: false };
    const currentTarget = targetData.hours;
    
    // Use isRestDay from API (respects aircraft rest_day_config)
    const isRestDay = targetData.isRestDay;
    
    // Calculate daily hours flown vs daily target
    const dailyActual = currentActual - prevActual;
    const dailyTarget = currentTarget - prevTarget;
    const dailyVariance = dailyActual - dailyTarget;
    
    // Include all days - rest days shown differently
    if (dailyActual > 0 || Math.abs(dailyTarget) > 0.5 || isRestDay) {
      varianceData.push({
        date,
        dateLabel: new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
        dayName: new Date(date).toLocaleDateString('en-GB', { weekday: 'short' }),
        dailyActual: Math.round(dailyActual * 10) / 10,
        dailyTarget: Math.round(dailyTarget * 10) / 10,
        variance: isRestDay ? 0 : Math.round(dailyVariance * 10) / 10, // No variance for rest days
        cumulativeActual: currentActual,
        cumulativeTarget: currentTarget,
        cumulativeVariance: Math.round((currentActual - currentTarget) * 10) / 10,
        isRestDay: isRestDay
      });
    }
    
    prevActual = currentActual;
    prevTarget = currentTarget;
  });

  // Take last 30 data points for display (most recent)
  const displayData = varianceData.slice(-30);
  const maxVariance = Math.max(...displayData.filter(d => !d.isRestDay).map(d => Math.abs(d.variance)), 5);

  return (
    <div className="variance-chart-3d">
      <div className="variance-chart-header">
        <BarChart3 size={20} />
        <h3>Daily Performance Variance</h3>
        <span className="variance-subtitle">Showing last {displayData.length} active days</span>
      </div>
      
      <div className="variance-bars-container">
        <div className="variance-axis-label positive">AHEAD</div>
        <div className="variance-axis-label negative">BEHIND</div>
        <div className="variance-center-line"></div>
        
        <div className="variance-bars-wrapper">
          {displayData.map((day, index) => {
            const isPositive = day.variance >= 0;
            const isRestDay = day.isRestDay;
            const barHeight = isRestDay ? 30 : (Math.abs(day.variance) / maxVariance) * 100;
            const intensity = Math.min(Math.abs(day.variance) / 3, 1);
            
            return (
              <div 
                key={day.date} 
                className={`variance-bar-column ${isRestDay ? 'rest-day' : (isPositive ? 'positive' : 'negative')}`}
                style={{ '--bar-index': index }}
              >
                {isRestDay ? (
                  // Rest day visual - centered indicator
                  <div className="rest-day-indicator">
                    <div className="rest-day-icon">🛏️</div>
                    <div className="rest-day-label">REST</div>
                  </div>
                ) : (
                  // Normal variance bar
                  <div className="bar-3d-wrapper" style={{ '--bar-height': `${barHeight}%` }}>
                    <div 
                      className={`bar-3d ${isPositive ? 'above' : 'below'}`}
                      style={{ 
                        '--intensity': intensity,
                        height: `${barHeight}%`
                      }}
                    >
                      <div className="bar-3d-front"></div>
                      <div className="bar-3d-top"></div>
                      <div className="bar-3d-side"></div>
                      <div className="bar-value">
                        {isPositive ? '+' : ''}{day.variance.toFixed(1)}h
                      </div>
                    </div>
                  </div>
                )}
                <div className={`bar-date ${isRestDay ? 'rest' : ''}`}>
                  {day.dateLabel}
                  {isRestDay && <span className="day-name">Sat</span>}
                </div>
                
                {/* Tooltip on hover */}
                <div className="bar-tooltip">
                  <div className={`tooltip-header ${isRestDay ? 'rest-day' : ''}`}>
                    {day.dateLabel} {day.dayName}
                    {isRestDay && <span className="rest-badge">CREW REST</span>}
                  </div>
                  {isRestDay ? (
                    <div className="tooltip-rest-message">
                      No target hours on rest days.<br/>
                      Saturday is designated crew rest.
                    </div>
                  ) : (
                    <>
                      <div className="tooltip-row">
                        <span>Flown:</span>
                        <span className="value">{day.dailyActual.toFixed(1)}h</span>
                      </div>
                      <div className="tooltip-row">
                        <span>Target:</span>
                        <span className="value">{day.dailyTarget.toFixed(1)}h</span>
                      </div>
                      <div className={`tooltip-row variance ${isPositive ? 'positive' : 'negative'}`}>
                        <span>Variance:</span>
                        <span className="value">{isPositive ? '+' : ''}{day.variance.toFixed(1)}h</span>
                      </div>
                    </>
                  )}
                  <div className="tooltip-divider"></div>
                  <div className="tooltip-row cumulative">
                    <span>Cumulative:</span>
                    <span className={`value ${day.cumulativeVariance >= 0 ? 'positive' : 'negative'}`}>
                      {day.cumulativeVariance >= 0 ? '+' : ''}{day.cumulativeVariance.toFixed(1)}h
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Legend */}
      <div className="variance-legend">
        <div className="legend-item positive">
          <div className="legend-color"></div>
          <span>Hours gained (above target)</span>
        </div>
        <div className="legend-item negative">
          <div className="legend-color"></div>
          <span>Hours lost (below target)</span>
        </div>
        {hasFixedRestDay && (
          <div className="legend-item rest">
            <div className="legend-color"></div>
            <span>Crew rest day (no target)</span>
          </div>
        )}
      </div>
    </div>
  );
};

const FleetStatus = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const darkMode = theme === 'dark';
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState("SPARROW3");
  const [error, setError] = useState(null);
  const [assetData, setAssetData] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  const [chartStyle, setChartStyle] = useState(() => {
    const saved = localStorage.getItem('fleetChartStyle');
    return saved || 'classic';
  });

  // Save chart style preference
  useEffect(() => {
    localStorage.setItem('fleetChartStyle', chartStyle);
  }, [chartStyle]);

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const transformApiData = (apiData) => {
    if (!apiData || apiData.error) return null;
    return {
      callsign: apiData.callsign,
      registration: apiData.registration,
      aircraftType: apiData.aircraft_type,
      deployment: apiData.deployment,
      base: apiData.base,
      restDayConfig: apiData.rest_day_config || 'saturday',  // 'saturday' or 'variable'
      contract: {
        startDate: apiData.contract?.start_date,
        endDate: apiData.contract?.end_date,
        endDateUndefined: apiData.contract?.end_date_undefined || false,
        contractedDays: apiData.contract?.contracted_days,
        startDateDisplay: formatDateForDisplay(apiData.contract?.start_date),
        endDateDisplay: apiData.contract?.end_date_undefined 
          ? "Until hours complete" 
          : formatDateForDisplay(apiData.contract?.end_date)
      },
      maintenance: {
        type: apiData.maintenance?.type,
        dueIn: apiData.maintenance?.due_in,
        margin: apiData.maintenance?.margin,
        estimateDate: apiData.maintenance?.estimate_date,
        estimatedDuration: apiData.maintenance?.estimated_duration
      },
      hours: {
        contracted: apiData.hours?.contracted,
        flown: apiData.hours?.flown,
        available: apiData.hours?.available
      },
      progress: {
        contractedDays: apiData.progress?.contracted_days,
        contractedHours: apiData.progress?.contracted_hours,
        ratio: apiData.progress?.ratio,
        avgWeeklyRequired: apiData.progress?.avg_weekly_required
      },
      stats: apiData.stats,
      trendData: apiData.trend_data,
      notes: apiData.notes
    };
  };

  const fetchFleetStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/fleet/status`);
      const data = response.data;
      const transformedData = {};
      for (const [key, value] of Object.entries(data)) {
        if (!value.error) {
          transformedData[key] = transformApiData(value);
        }
      }
      setAssetData(transformedData);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Error fetching fleet status:", err);
      setError("Failed to load fleet status data");
      toast.error("Failed to load fleet status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFleetStatus();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const currentAsset = assetData[selectedAsset];

  const formatDate = (date) => date.toLocaleDateString('en-GB', { 
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' 
  });

  const formatTime = (date) => date.toLocaleTimeString('en-GB', { 
    hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'UTC'
  }) + ' UTC';

  const handleRefresh = async () => {
    await fetchFleetStatus();
    toast.success("Fleet status refreshed");
  };

  // State for PDF remarks dialog
  const [showRemarksDialog, setShowRemarksDialog] = useState(false);
  const [pdfRemarks, setPdfRemarks] = useState('');

  const handleDownloadPDF = async () => {
    if (!selectedAsset) return;
    // Show remarks dialog instead of downloading directly
    setShowRemarksDialog(true);
  };

  const handleConfirmDownload = async () => {
    if (!selectedAsset) return;
    
    try {
      setShowRemarksDialog(false);
      toast.info("Generating PDF report...");
      
      // Encode remarks for URL
      const remarksParam = pdfRemarks ? `?remarks=${encodeURIComponent(pdfRemarks)}` : '';
      
      const response = await axios.get(`${API}/fleet/report/${selectedAsset}/pdf${remarksParam}`, {
        responseType: 'arraybuffer',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      // Check if response is valid
      if (!response.data || response.data.byteLength === 0) {
        throw new Error('Empty PDF response');
      }
      
      // Create blob from arraybuffer
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const reportDate = new Date().toLocaleDateString('en-GB').replace(/\//g, '');
      link.setAttribute('download', `FSA_report_${selectedAsset.toUpperCase()}_${reportDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("PDF report downloaded");
      setPdfRemarks(''); // Reset remarks
    } catch (err) {
      console.error("Error downloading PDF:", err);
      toast.error(`Failed to generate PDF: ${err.message || 'Unknown error'}`);
    }
  };

  const handleCancelDownload = () => {
    setShowRemarksDialog(false);
    setPdfRemarks('');
  };

  const getProgressWidth = (percentStr) => {
    const num = parseFloat(percentStr);
    return isNaN(num) ? 0 : Math.min(num, 100);
  };

  const parseHours = (hourStr) => {
    if (!hourStr) return 0;
    const parts = hourStr.split(':');
    return parseInt(parts[0]) + (parts[1] ? parseInt(parts[1]) / 60 : 0);
  };

  // Format hours to HH:MM string - handles negative values correctly
  const formatHoursToHHMM = (value, showSign = false) => {
    if (value === null || value === undefined) return '-';
    const absValue = Math.abs(value);
    const hours = Math.floor(absValue);
    const mins = Math.floor((absValue - hours) * 60);
    const sign = value < 0 ? '-' : (showSign && value > 0 ? '+' : '');
    return `${sign}${hours}:${mins.toString().padStart(2, '0')}`;
  };

  // Use raw hours from stats/trend_data when available for precise calculations
  // Fall back to parsing formatted string if raw value not available
  const contractedHours = currentAsset?.stats?.contracted_hours_raw || 
                          currentAsset?.trend_data?.contracted_hours || 
                          parseHours(currentAsset?.hours?.contracted);
  const flownHoursRaw = currentAsset?.stats?.flown_hours_raw;
  const flownHours = flownHoursRaw !== undefined ? flownHoursRaw : parseHours(currentAsset?.hours?.flown);
  const hoursProgress = contractedHours > 0 ? (flownHours / contractedHours * 100) : 0;

  // Calculate performance metrics - Enhanced for clarity
  // Respects rest day configuration per aircraft (saturday or variable)
  const calculatePerformance = () => {
    if (!currentAsset?.contract || !currentAsset?.hours) {
      return null;
    }

    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      
      // Handle ISO format (YYYY-MM-DD)
      if (dateStr.includes('-')) {
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? null : date;
      }
      
      // Handle DD/MM/YYYY format
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(parts[2], parts[1] - 1, parts[0]);
      }
      
      return null;
    };

    const contractStart = parseDate(currentAsset.contract.startDate);
    const endDateUndefined = currentAsset.contract.endDateUndefined;
    let contractEnd;
    
    if (endDateUndefined) {
      // No fixed end date — use today + 7 days for calculations
      contractEnd = new Date();
      contractEnd.setDate(contractEnd.getDate() + 7);
    } else {
      contractEnd = parseDate(currentAsset.contract.endDate);
    }
    
    if (!contractStart || !contractEnd) return null;

    const today = new Date();
    const totalDays = Math.ceil((contractEnd - contractStart) / (1000 * 60 * 60 * 24));
    const daysElapsed = Math.ceil((today - contractStart) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.max(0, Math.ceil((contractEnd - today) / (1000 * 60 * 60 * 24)));

    // Get rest day config - 'saturday' means fixed Saturday rest, 'variable' means all days count
    const restDayConfig = currentAsset.restDayConfig || 'saturday';
    const hasFixedRestDay = restDayConfig === 'saturday';

    // Count working days - exclude Saturdays only if hasFixedRestDay
    // Saturday is day 6 in JavaScript (0=Sunday, 6=Saturday)
    const countWorkingDays = (startDate, endDate) => {
      let count = 0;
      let current = new Date(startDate);
      while (current <= endDate) {
        if (hasFixedRestDay && current.getDay() === 6) {
          // Skip Saturday for fixed rest day config
        } else {
          count++;
        }
        current.setDate(current.getDate() + 1);
      }
      return count;
    };

    const totalWorkingDays = countWorkingDays(contractStart, contractEnd);
    const workingDaysElapsed = countWorkingDays(contractStart, today > contractEnd ? contractEnd : today);
    const workingDaysRemaining = countWorkingDays(today, contractEnd);
    
    // Calculate expected hours based on WORKING days only
    const progressRatio = Math.min(workingDaysElapsed / totalWorkingDays, 1);
    const expectedHours = contractedHours * progressRatio;
    const varianceHours = flownHours - expectedHours;
    
    // Daily rate based on WORKING days
    const dailyRateRequired = totalWorkingDays > 0 ? contractedHours / totalWorkingDays : 0;
    const daysVariance = dailyRateRequired > 0 ? varianceHours / dailyRateRequired : 0;
    
    // Efficiency based on working days
    const actualDailyRate = workingDaysElapsed > 0 ? flownHours / workingDaysElapsed : 0;
    const efficiency = dailyRateRequired > 0 ? (actualDailyRate / dailyRateRequired) * 100 : 100;
    
    // Hours remaining - use API value if available, otherwise calculate
    const hoursRemaining = currentAsset?.hours?.available 
      ? parseHours(currentAsset.hours.available)
      : Math.max(0, contractedHours - flownHours);
    
    // Required daily rate to finish on time (only working days)
    const requiredDailyRateToFinish = workingDaysRemaining > 0 ? hoursRemaining / workingDaysRemaining : 0;
    
    // Burn rate comparison
    const burnRateRatio = dailyRateRequired > 0 ? actualDailyRate / dailyRateRequired : 1;
    
    // Projected hours at contract end (at current rate)
    // This tells us: "If we continue at current rate, how many hours will we have at contract end?"
    const projectedHoursAtEnd = actualDailyRate > 0 
      ? flownHours + (actualDailyRate * workingDaysRemaining)
      : flownHours;
    
    // Variance at contract end - positive means ahead, negative means behind
    const projectedVarianceAtEnd = projectedHoursAtEnd - contractedHours;
    
    // Convert to days equivalent (how many days of flying is the variance)
    const projectedDaysVarianceAtEnd = dailyRateRequired > 0 
      ? projectedVarianceAtEnd / dailyRateRequired 
      : 0;
    
    // Keep contract end as the reference date
    const projectedCompletion = contractEnd;
    // daysLate is now the days-equivalent of hours behind (negative variance = late)
    const daysLate = Math.round(projectedDaysVarianceAtEnd);
    
    // Weekly performance (hours per week based on actual rate)
    const hoursPerWeek = actualDailyRate * 7;
    const targetHoursPerWeek = dailyRateRequired * 7;
    
    // Contract completion percentage
    const contractCompletion = (daysElapsed / totalDays) * 100;
    const hoursCompletion = (flownHours / contractedHours) * 100;

    return {
      expectedHours,
      varianceHours,
      daysVariance,
      efficiency,
      projectedCompletion,
      daysLate,
      isAhead: varianceHours >= 0,
      daysElapsed,
      daysRemaining,
      totalDays,
      actualDailyRate,
      dailyRateRequired,
      hoursRemaining,
      requiredDailyRateToFinish,
      burnRateRatio,
      hoursPerWeek,
      targetHoursPerWeek,
      contractCompletion,
      hoursCompletion,
      // New metrics for projected variance at contract end
      projectedHoursAtEnd,
      projectedVarianceAtEnd,
      projectedDaysVarianceAtEnd,
      hasFixedRestDay,
      totalWorkingDays,
      workingDaysRemaining,
      endDateUndefined
    };
  };

  const performance = calculatePerformance();

  // Calculate seasonal performance analysis (Winter, Spring, Summer, Fall)
  const calculateSeasonalAnalysis = () => {
    // Check if we have trend data - using trendData (camelCase) as stored in state
    if (!currentAsset?.trendData?.actual || !currentAsset?.trendData?.target) {
      console.log('SeasonalAnalysis: No trend data available', currentAsset?.trendData);
      return null;
    }

    const actual = currentAsset.trendData.actual;
    const target = currentAsset.trendData.target;
    
    if (!actual.length || !target.length) {
      console.log('SeasonalAnalysis: Empty trend arrays');
      return null;
    }

    console.log('SeasonalAnalysis: Processing', actual.length, 'actual points,', target.length, 'target points');

    // Define seasons (Northern Hemisphere / Europe)
    const getSeason = (month) => {
      if (month >= 2 && month <= 4) return { key: 'spring', label: 'Spring', icon: '🌸', months: 'Mar-May' };
      if (month >= 5 && month <= 7) return { key: 'summer', label: 'Summer', icon: '☀️', months: 'Jun-Aug' };
      if (month >= 8 && month <= 10) return { key: 'fall', label: 'Fall', icon: '🍂', months: 'Sep-Nov' };
      return { key: 'winter', label: 'Winter', icon: '❄️', months: 'Dec-Feb' };
    };

    // Group data by season-year
    const seasonalData = {};
    
    // Process actual data
    let prevActualHours = 0;
    actual.forEach((point) => {
      const dateParts = point.date.split('-');
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // 0-indexed
      const season = getSeason(month);
      
      // For winter, use the year of Jan/Feb, not Dec
      const seasonYear = (season.key === 'winter' && month === 11) ? year + 1 : year;
      const seasonKey = `${seasonYear}-${season.key}`;
      const seasonLabel = `${season.label} ${String(seasonYear).slice(-2)}`;
      
      if (!seasonalData[seasonKey]) {
        seasonalData[seasonKey] = {
          key: seasonKey,
          season: season.key,
          label: seasonLabel,
          icon: season.icon,
          months: season.months,
          year: seasonYear,
          startActual: prevActualHours,
          startTarget: 0,
          endActual: 0,
          endTarget: 0,
          days: 0,
          order: season.key === 'winter' ? 0 : season.key === 'spring' ? 1 : season.key === 'summer' ? 2 : 3
        };
      }
      
      seasonalData[seasonKey].days++;
      seasonalData[seasonKey].endActual = point.hours;
      prevActualHours = point.hours;
    });

    // Process target data
    let prevTargetHours = 0;
    target.forEach((point) => {
      const dateParts = point.date.split('-');
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1;
      const season = getSeason(month);
      const seasonYear = (season.key === 'winter' && month === 11) ? year + 1 : year;
      const seasonKey = `${seasonYear}-${season.key}`;
      
      if (seasonalData[seasonKey]) {
        if (seasonalData[seasonKey].startTarget === 0 && prevTargetHours > 0) {
          seasonalData[seasonKey].startTarget = prevTargetHours;
        }
        seasonalData[seasonKey].endTarget = point.hours;
      }
      prevTargetHours = point.hours;
    });

    // Calculate variance per season
    const seasonalAnalysis = Object.values(seasonalData)
      .map(s => {
        const actualGained = s.endActual - s.startActual;
        const targetExpected = s.endTarget - s.startTarget;
        const variance = actualGained - targetExpected;
        
        return {
          ...s,
          actualHours: Math.round(actualGained * 10) / 10,
          targetHours: Math.round(targetExpected * 10) / 10,
          variance: Math.round(variance * 10) / 10,
          performance: targetExpected > 0 ? Math.round((actualGained / targetExpected) * 100) : 100
        };
      })
      .filter(s => s.days > 0)
      .sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.order - b.order;
      });

    console.log('SeasonalAnalysis: Calculated', seasonalAnalysis.length, 'seasons');

    // Find best and worst seasons
    const sortedByVariance = [...seasonalAnalysis].sort((a, b) => b.variance - a.variance);
    const bestSeasons = sortedByVariance.filter(s => s.variance > 0).slice(0, 2);
    const worstSeasons = sortedByVariance.filter(s => s.variance < 0).slice(-2).reverse();

    return {
      seasons: seasonalAnalysis,
      bestSeasons,
      worstSeasons,
      totalSeasons: seasonalAnalysis.length
    };
  };

  const seasonalAnalysis = calculateSeasonalAnalysis();

  return (
    <div className={`fleet-status-container ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`} data-testid="fleet-status-page">
      {/* PDF Remarks Dialog */}
      {showRemarksDialog && (
        <div className="pdf-remarks-overlay" data-testid="pdf-remarks-dialog">
          <div className="pdf-remarks-dialog">
            <h3>Add Remarks to Report</h3>
            <p>Enter any remarks you'd like to include in the PDF report (optional):</p>
            <textarea
              value={pdfRemarks}
              onChange={(e) => setPdfRemarks(e.target.value)}
              placeholder="Enter remarks here..."
              rows={4}
              maxLength={300}
              autoFocus
            />
            <div className="pdf-remarks-char-count">{pdfRemarks.length}/300 characters</div>
            <div className="pdf-remarks-buttons">
              <button className="pdf-remarks-cancel" onClick={handleCancelDownload}>
                Cancel
              </button>
              <button className="pdf-remarks-confirm" onClick={handleConfirmDownload}>
                <Download size={16} />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="fleet-header">
        <div className="fleet-header-left">
          <img 
            src={NSAS_LOGO} 
            alt="NSAS" 
            className="fleet-logo" 
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
            title="Back to Home"
          />
        </div>
        <div className="fleet-header-center">
          <div className="fleet-date">{formatDate(currentTime)}</div>
          <div className="fleet-time">{formatTime(currentTime)}</div>
        </div>
        <div className="fleet-header-right">
          <h1 className="fleet-title">Fleet Status</h1>
          <div className="fleet-refresh-info">
            <span className={`refresh-indicator ${loading ? 'loading' : ''}`}></span>
            {lastRefresh ? lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
          </div>
          <button className="fleet-refresh-btn" onClick={handleRefresh} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={loading ? 'spinning' : ''} size={16} />
          </button>
          <button 
            className="theme-toggle-btn" 
            onClick={toggleTheme} 
            data-testid="theme-toggle-btn"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </header>

      {/* Asset Tabs */}
      <div className="asset-selector-bar">
        <div className="asset-tabs">
          {Object.keys(assetData).map((assetKey) => (
            <button
              key={assetKey}
              className={`asset-tab ${selectedAsset === assetKey ? 'active' : ''}`}
              onClick={() => setSelectedAsset(assetKey)}
              data-testid={`asset-tab-${assetKey.toLowerCase()}`}
            >
              <Plane size={14} />
              {assetKey}
            </button>
          ))}
        </div>
        <div className="asset-bar-right">
          <button 
            className="download-pdf-btn" 
            onClick={handleDownloadPDF}
            disabled={loading || !currentAsset}
            data-testid="download-pdf-btn"
          >
            <Download size={14} />
            Download PDF
          </button>
          <div className="report-badge">
            <FileText size={12} />
            FRONTEX Surveillance Asset
          </div>
        </div>
      </div>

      {/* Main Content */}
      {loading && !currentAsset ? (
        <div className="fleet-loading">
          <RefreshCw className="spinning" size={40} />
          <p>Loading fleet status...</p>
        </div>
      ) : error ? (
        <div className="fleet-error">
          <AlertCircle size={40} />
          <p>{error}</p>
          <button onClick={handleRefresh}>Retry</button>
        </div>
      ) : !currentAsset ? (
        <div className="fleet-error">
          <AlertCircle size={40} />
          <p>No data available for {selectedAsset}</p>
        </div>
      ) : (
        <>
          {/* Asset Info Strip */}
          <div className="asset-info-bar">
            <div className="asset-info-item">
              <span className="info-label">Callsign</span>
              <span className="info-value callsign">{currentAsset?.callsign}</span>
            </div>
            <div className="asset-info-item">
              <span className="info-label">Registration</span>
              <span className="info-value">{currentAsset?.registration}</span>
            </div>
            <div className="asset-info-item">
              <span className="info-label">Aircraft</span>
              <span className="info-value">{currentAsset?.aircraftType}</span>
            </div>
            <div className="asset-info-item">
              <span className="info-label">Deployment</span>
              <span className="info-value">{currentAsset?.deployment}</span>
            </div>
            <div className="asset-info-item">
              <span className="info-label">Base</span>
              <span className="info-value highlight">{currentAsset?.base}</span>
            </div>
          </div>

          {/* Main Chart */}
          <div className="fleet-chart-main">
            <div className="chart-main-card">
              <div className="chart-main-header">
                <div className="chart-title-section">
                  <TrendingUp size={20} />
                  <h2>Contract Progress — {currentAsset.callsign}</h2>
                </div>
                <div className="chart-controls">
                  <div className="chart-style-selector">
                    {CHART_STYLES.map((style) => {
                      const IconComponent = style.icon;
                      return (
                        <button
                          key={style.id}
                          className={`chart-style-btn ${chartStyle === style.id ? 'active' : ''}`}
                          onClick={() => setChartStyle(style.id)}
                          title={style.name}
                          data-testid={`chart-style-${style.id}`}
                        >
                          <IconComponent size={14} />
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="chart-stats">
                  <div className="stat-item">
                    <span className="stat-value flown">{currentAsset?.hours?.flown}</span>
                    <span className="stat-label">Flown</span>
                  </div>
                  <div className="stat-divider">/</div>
                  <div className="stat-item">
                    <span className="stat-value contracted">{currentAsset?.hours?.contracted}</span>
                    <span className="stat-label">Target</span>
                  </div>
                  <div className="stat-progress">
                    <span className="stat-pct">{currentAsset?.progress?.contractedHours}</span>
                  </div>
                </div>
              </div>
              <div className="chart-main-body">
                {currentAsset?.trendData ? (
                  <TrendChart 
                    trendData={currentAsset.trendData} 
                    contractedHours={parseHours(currentAsset?.hours?.contracted)}
                    chartStyle={chartStyle}
                    darkMode={darkMode}
                  />
                ) : (
                  <div className="chart-loading">Loading trend data...</div>
                )}
              </div>
            </div>
          </div>

          {/* 3D Variance Bar Chart */}
          {currentAsset?.trendData && (
            <VarianceBarChart3D 
              trendData={currentAsset.trendData}
              darkMode={darkMode}
              hasFixedRestDay={currentAsset.restDayConfig === 'saturday'}
            />
          )}

          {/* Info Cards */}
          <div className="fleet-info-row">
            {/* Contract */}
            <div className="fleet-info-card">
              <div className="info-card-header">
                <Calendar size={12} />
                <span>Contract Period</span>
              </div>
              <div className="info-card-content">
                <div className="info-row">
                  <span>Start</span>
                  <span>{currentAsset?.contract?.startDateDisplay || currentAsset?.contract?.startDate}</span>
                </div>
                <div className="info-row">
                  <span>End</span>
                  <span>{currentAsset?.contract?.endDateDisplay || currentAsset?.contract?.endDate}</span>
                </div>
                <div className="info-row">
                  <span>{currentAsset?.contract?.endDateUndefined ? 'Days elapsed' : 'Duration'}</span>
                  <span>{currentAsset?.contract?.contractedDays} days</span>
                </div>
              </div>
              <div className="info-card-progress">
                <div className="progress-bar-thin">
                  <div className="progress-bar-thin-fill days" style={{ width: `${getProgressWidth(currentAsset?.progress?.contractedDays)}%` }}></div>
                </div>
                <span className="progress-text">{currentAsset?.progress?.contractedDays}</span>
              </div>
            </div>

            {/* Hours */}
            <div className="fleet-info-card hours">
              <div className="info-card-header">
                <Clock size={12} />
                <span>Flight Hours</span>
              </div>
              <div className="info-card-content hours-content">
                <div className="hours-stat">
                  <span className="hours-big flown">{currentAsset?.hours?.flown}</span>
                  <span className="hours-unit">flown</span>
                </div>
                <div className="hours-remaining">
                  <span className="hours-avail">{currentAsset?.hours?.available}</span>
                  <span className="hours-unit">remaining</span>
                </div>
              </div>
              <div className="info-card-progress">
                <div className="progress-bar-thin">
                  <div className="progress-bar-thin-fill hours" style={{ width: `${hoursProgress}%` }}></div>
                </div>
                <span className="progress-text">{currentAsset?.progress?.contractedHours}</span>
              </div>
            </div>

            {/* Maintenance */}
            <div className="fleet-info-card">
              <div className="info-card-header">
                <Wrench size={12} />
                <span>Maintenance</span>
              </div>
              <div className="info-card-content">
                <div className="maintenance-due">
                  <span className="maint-label">Due in</span>
                  <span className="maint-value warning">{currentAsset?.maintenance?.dueIn}</span>
                </div>
                <div className="info-row small">
                  <span>Type</span>
                  <span>{currentAsset?.maintenance?.type}</span>
                </div>
                <div className="info-row small">
                  <span>Est. date</span>
                  <span>{currentAsset?.maintenance?.estimateDate}</span>
                </div>
              </div>
            </div>

            {/* Rate */}
            <div className="fleet-info-card">
              <div className="info-card-header">
                <TrendingUp size={12} />
                <span>Required Rate</span>
              </div>
              <div className="info-card-content">
                <div className="rate-main">
                  <span className="rate-value">{currentAsset?.progress?.ratio}</span>
                  <span className="rate-unit">per day</span>
                </div>
                <div className="rate-weekly">
                  <span className="rate-weekly-value">{currentAsset?.progress?.avgWeeklyRequired}</span>
                  <span className="rate-weekly-label">weekly avg needed</span>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Section - Redesigned for Clarity */}
          {performance && (
            <div className="performance-section">
              <div className="performance-header">
                <Target size={20} />
                <h3>Contract Performance Dashboard</h3>
                <span className={`performance-status ${performance.isAhead ? 'ahead' : 'behind'}`}>
                  {performance.isAhead ? '● ON TRACK' : '● ATTENTION NEEDED'}
                </span>
              </div>
              
              {/* Main KPI Row - Large, Clear Numbers */}
              <div className="performance-kpi-grid">
                <div className={`kpi-large ${performance.isAhead ? 'positive' : 'negative'}`}>
                  <div className="kpi-large-label">Current vs Target</div>
                  <div className="kpi-large-value">
                    {formatHoursToHHMM(performance.varianceHours, true)}
                  </div>
                  <div className="kpi-large-unit">
                    {performance.isAhead ? 'ahead of where you should be today' : 'behind where you should be today'}
                  </div>
                </div>
                
                <div className="kpi-large neutral">
                  <div className="kpi-large-label">Average Daily Rate</div>
                  <div className="kpi-large-value">
                    {performance.actualDailyRate.toFixed(1)}h
                  </div>
                  <div className="kpi-large-unit">
                    vs {performance.dailyRateRequired.toFixed(1)}h/day required to meet target
                  </div>
                </div>
                
                <div className={`kpi-large ${performance.projectedVarianceAtEnd >= 0 ? 'positive' : 'negative'}`}>
                  <div className="kpi-large-label">Projected at Contract End</div>
                  <div className="kpi-large-value">
                    {formatHoursToHHMM(performance.projectedVarianceAtEnd, true)}
                  </div>
                  <div className="kpi-large-unit">
                    {performance.projectedVarianceAtEnd >= 0 
                      ? `if current rate continues until ${performance.projectedCompletion.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
                      : `if current rate continues until ${performance.projectedCompletion.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
                    }
                  </div>
                </div>
              </div>

              {/* Secondary Metrics Row */}
              <div className="performance-metrics-row">
                <div className="metric-card">
                  <Clock size={16} />
                  <div className="metric-info">
                    <span className="metric-label">Hours Flown</span>
                    <span className="metric-value">{currentAsset?.hours?.flown}</span>
                  </div>
                </div>
                <div className="metric-card">
                  <Target size={16} />
                  <div className="metric-info">
                    <span className="metric-label">Target Hours by Today</span>
                    <span className="metric-value">
                      {formatHoursToHHMM(performance.expectedHours)}
                    </span>
                  </div>
                </div>
                <div className="metric-card">
                  <TrendingUp size={16} />
                  <div className="metric-info">
                    <span className="metric-label">Hours Left to Fly</span>
                    <span className="metric-value">
                      {formatHoursToHHMM(performance.hoursRemaining)}
                    </span>
                  </div>
                </div>
                <div className="metric-card">
                  <Calendar size={16} />
                  <div className="metric-info">
                    <span className="metric-label">Working Days Left</span>
                    <span className="metric-value">{performance.workingDaysRemaining}</span>
                  </div>
                </div>
                <div className="metric-card">
                  <Activity size={16} />
                  <div className="metric-info">
                    <span className="metric-label">Rate Efficiency</span>
                    <span className={`metric-value ${performance.efficiency >= 100 ? 'positive' : performance.efficiency >= 90 ? '' : 'warning'}`}>
                      {performance.efficiency.toFixed(0)}%
                    </span>
                  </div>
                </div>
                <div className="metric-card">
                  <Gauge size={16} />
                  <div className="metric-info">
                    <span className="metric-label">Rate Needed to Finish</span>
                    <span className={`metric-value ${performance.requiredDailyRateToFinish <= performance.actualDailyRate ? 'positive' : 'warning'}`}>
                      {performance.requiredDailyRateToFinish.toFixed(1)}h/day
                    </span>
                  </div>
                </div>
              </div>

              {/* Progress Bars */}
              <div className="performance-progress-section">
                <div className="progress-item">
                  <div className="progress-header">
                    <span className="progress-label">Contract Timeline</span>
                    <span className="progress-value">{performance.contractCompletion.toFixed(0)}% elapsed ({performance.daysElapsed} of {performance.totalDays} days)</span>
                  </div>
                  <div className="progress-bar-container">
                    <div className="progress-bar-fill timeline" style={{ width: `${Math.min(performance.contractCompletion, 100)}%` }}></div>
                  </div>
                </div>
                <div className="progress-item">
                  <div className="progress-header">
                    <span className="progress-label">Hours Completion</span>
                    <span className="progress-value">{performance.hoursCompletion.toFixed(1)}% ({currentAsset?.hours?.flown} of {currentAsset?.hours?.contracted})</span>
                  </div>
                  <div className="progress-bar-container">
                    <div 
                      className={`progress-bar-fill hours ${performance.hoursCompletion >= performance.contractCompletion ? 'ahead' : 'behind'}`} 
                      style={{ width: `${Math.min(performance.hoursCompletion, 100)}%` }}
                    ></div>
                    {/* Target marker */}
                    <div className="progress-target-marker" style={{ left: `${Math.min(performance.contractCompletion, 100)}%` }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Seasonal Performance Analysis Panel */}
          {seasonalAnalysis && seasonalAnalysis.seasons.length > 0 && (
            <div className="seasonal-analysis-section">
              <div className="seasonal-header">
                <BarChart3 size={20} />
                <h3>Seasonal Performance Analysis</h3>
                <span className="seasonal-subtitle">Hours gained/lost vs target by season</span>
              </div>
              
              {/* Seasonal Chart */}
              <div className="seasonal-chart">
                {seasonalAnalysis.seasons.map((season) => {
                  const maxVariance = Math.max(...seasonalAnalysis.seasons.map(s => Math.abs(s.variance)), 1);
                  const barWidth = (Math.abs(season.variance) / maxVariance) * 100;
                  const isPositive = season.variance >= 0;
                  
                  return (
                    <div key={season.key} className="season-bar-row">
                      <div className="season-label">
                        <span className="season-icon">{season.icon}</span>
                        <span className="season-name">{season.label}</span>
                      </div>
                      <div className="season-bar-container">
                        <div className="season-bar-center"></div>
                        {isPositive ? (
                          <div 
                            className="season-bar positive" 
                            style={{ 
                              width: `${barWidth / 2}%`,
                              left: '50%'
                            }}
                          >
                            <span className="bar-value">+{season.variance.toFixed(1)}h</span>
                          </div>
                        ) : (
                          <div 
                            className="season-bar negative" 
                            style={{ 
                              width: `${barWidth / 2}%`,
                              right: '50%'
                            }}
                          >
                            <span className="bar-value">{season.variance.toFixed(1)}h</span>
                          </div>
                        )}
                      </div>
                      <div className="season-stats">
                        <span className={`season-performance ${season.performance >= 100 ? 'good' : season.performance >= 80 ? 'ok' : 'poor'}`}>
                          {season.performance}%
                        </span>
                        <span className="season-hours">{season.actualHours.toFixed(0)}h flown</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Best/Worst Summary */}
              <div className="seasonal-summary">
                <div className="summary-column best">
                  <div className="summary-title">
                    <TrendingUp size={16} />
                    <span>Best Performing Seasons</span>
                  </div>
                  {seasonalAnalysis.bestSeasons.length > 0 ? (
                    seasonalAnalysis.bestSeasons.map(s => (
                      <div key={s.key} className="summary-item">
                        <span className="summary-season">
                          <span className="season-icon-small">{s.icon}</span> {s.label}
                        </span>
                        <span className="summary-value positive">+{s.variance.toFixed(1)}h</span>
                      </div>
                    ))
                  ) : (
                    <div className="summary-item empty">No seasons ahead of target</div>
                  )}
                </div>
                <div className="summary-column worst">
                  <div className="summary-title">
                    <AlertCircle size={16} />
                    <span>Seasons Needing Attention</span>
                  </div>
                  {seasonalAnalysis.worstSeasons.length > 0 ? (
                    seasonalAnalysis.worstSeasons.map(s => (
                      <div key={s.key} className="summary-item">
                        <span className="summary-season">
                          <span className="season-icon-small">{s.icon}</span> {s.label}
                        </span>
                        <span className="summary-value negative">{s.variance.toFixed(1)}h</span>
                      </div>
                    ))
                  ) : (
                    <div className="summary-item empty">All seasons on or ahead of target</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {currentAsset?.notes && (
            <div className="fleet-notes-bar">
              <FileText size={14} />
              <span>{currentAsset.notes}</span>
            </div>
          )}
        </>
      )}

      {/* Footer */}
      <footer className="fleet-footer">
        <div className="footer-left">NSAS - North Sea Aviation Services</div>
        <div className="footer-center">NSAS TOOLBOX</div>
        <div className="footer-right">{formatDate(currentTime)} {formatTime(currentTime)}</div>
      </footer>
    </div>
  );
};

export default FleetStatus;
